import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RegionService } from 'src/app/services/region.service';
import { DivisionService } from 'src/app/services/division.service';
import { AdministrativeMappingService } from 'src/app/services/administrative-mapping.service';
import { UserSessionService } from 'src/app/services/usersession.service';
import { LotteryService, LotteryBookingListResponse, CommunityTarget, CommunityLotteryRun } from 'src/app/services/lottery.service';
import { VehicleService } from 'src/app/services/vehicle.service';
import { PaccsbookingService } from 'src/app/services/paccsbooking.service';
import { ExcelService } from 'src/app/services/excel.service';
import { LookUpResponse } from 'src/app/models/lookup';
import Swal from 'sweetalert2';

interface LotteryResultRow {
  applicationId: string;
  weight: number;
  communityType: number;
  communityName: string;
  rank: number;
  selected: boolean;
}

interface LotteryApplication {
  id: string;
  communityType: number;
  community: string;
}

interface LotteryParticipant extends LotteryApplication {
  status: string;
}

@Component({
  selector: 'app-run-lottery',
  templateUrl: './run-lottery.component.html',
  styleUrls: ['./run-lottery.component.scss']
})
export class RunLotteryComponent implements OnInit {

  form: FormGroup;
  isShow = false;
  isResultShown = false;

  financialYearList: LookUpResponse[] = [];
  filterFinancialYearList: LookUpResponse[] = [];

  regionList: LookUpResponse[] = [];
  filterRegionList: LookUpResponse[] = [];

  divisionList: LookUpResponse[] = [];
  filterDivisionList: LookUpResponse[] = [];

  subDivisionList: LookUpResponse[] = [];
  filterSubDivisionList: LookUpResponse[] = [];

  villageList: LookUpResponse[] = [];
  filterVillageList: LookUpResponse[] = [];

  departmentList: LookUpResponse[] = [];
  filterDepartmentList: LookUpResponse[] = [];

  goList: LookUpResponse[] = [];
  filterGoList: LookUpResponse[] = [];

  schemeList: LookUpResponse[] = [];
  filterSchemeList: LookUpResponse[] = [];

  componentList: LookUpResponse[] = [];
  filterComponentList: LookUpResponse[] = [];

  productList: LookUpResponse[] = [];
  filterProductList: LookUpResponse[] = [];

  regionId: any;
  divisionId: any;
  subDivisionId: any;

  isLoadingApplicants = false;
  isRunningLottery = false;

  // Grand total across communities - kept for the overall "No. of Targets" chip.
  noOfTargets: number = null;
  // Per-community breakdown driving the actual per-community draw and its display.
  communityTargets: CommunityTarget[] = [];
  communityResults: CommunityLotteryRun[] = [];

  applications: LotteryApplication[] = [];
  selectedParticipants: LotteryParticipant[] = [];
  notSelectedParticipants: LotteryParticipant[] = [];

  applicationColumns: string[] = ['id', 'community'];
  participantColumns: string[] = ['id', 'community', 'status'];

  applicationsDataSource = new MatTableDataSource<LotteryApplication>([]);
  selectedDataSource = new MatTableDataSource<LotteryParticipant>([]);
  notSelectedDataSource = new MatTableDataSource<LotteryParticipant>([]);

  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('selectedPaginator', { static: false }) selectedPaginator: MatPaginator;
  @ViewChild('notSelectedPaginator', { static: false }) notSelectedPaginator: MatPaginator;

  constructor(
    private formBuilder: FormBuilder,
    private regionService: RegionService,
    private divisionService: DivisionService,
    private administrativeMappingService: AdministrativeMappingService,
    private userSessionService: UserSessionService,
    private lotteryService: LotteryService,
    private vehicleService: VehicleService,
    private paccsbookingService: PaccsbookingService,
    private excelService: ExcelService
  ) {
    this.regionId = this.userSessionService.getFilterregionId();
    this.divisionId = this.userSessionService.getFilterDivisionId();
    this.subDivisionId = this.userSessionService.getFilterSubDivisionId();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.getFinancialYear(true);
  }

  initializeForm(): void {
    this.form = this.formBuilder.group({
      financialYearId: [null, Validators.required],
      regionId: [null, Validators.required],
      divisionId: [null, Validators.required],
      subDivisionId: [null, Validators.required],
      villageId: [null, Validators.required],
      departmentId: [null, Validators.required],
      dbtGoId: [null, Validators.required],
      dbtSchemeId: [null, Validators.required],
      dbtComponentId: [null, Validators.required],
      dbtProductId: [null, Validators.required]
    });
  }

  getFinancialYear(refresh: boolean): void {
    this.regionService.getFinYear(refresh).subscribe(result => {
      if (!result) {
        return;
      }
      this.financialYearList = result;
      this.filterFinancialYearList = this.financialYearList.slice();

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      let selectedYear: any;
      if (currentMonth >= 4) {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear && Number(x.endYear) === currentYear + 1);
      } else {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear - 1 && Number(x.endYear) === currentYear);
      }
      if (!selectedYear && this.financialYearList.length > 0) {
        selectedYear = this.financialYearList[this.financialYearList.length - 1];
      }
      if (selectedYear) {
        this.form.controls['financialYearId'].setValue(selectedYear.key);
      }

      this.regionLookup();
      this.departmentLookup();
    });
  }

  onFinancialYearChange(): void {
    this.resetFrom('regionId');
    this.regionLookup();

    // Department stays selected (it doesn't depend on financial year) - only
    // GO-downward needs to be re-fetched since GO is scoped by financial year.
    this.resetSubsidyFrom('dbtGoId');
    this.goLookup();
  }

  private regionLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getRegions(financialYearId, true).subscribe(result => {
      this.regionList = result || [];
      this.filterRegionList = this.regionList.slice();

      if (this.regionId > 0 && this.regionList.length > 1) {
        this.form.controls['regionId'].setValue(this.regionId);
        this.onRegionChange();
      } else if (this.regionList.length === 1) {
        this.form.controls['regionId'].setValue(this.regionList[0].key);
        this.onRegionChange();
      }
    });
  }

  onRegionChange(): void {
    this.resetFrom('divisionId');
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getDivisions(this.form.value.regionId, financialYearId, true).subscribe(result => {
      this.divisionList = result || [];
      this.filterDivisionList = this.divisionList.slice();

      if (this.divisionId > 0 && this.divisionList.length > 1) {
        this.form.controls['divisionId'].setValue(this.divisionId);
        this.onDivisionChange();
      } else if (this.divisionList.length === 1) {
        this.form.controls['divisionId'].setValue(this.divisionList[0].key);
        this.onDivisionChange();
      }
    });
  }

  onDivisionChange(): void {
    this.resetFrom('subDivisionId');
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getSubDivisions(this.form.value.divisionId, financialYearId, true).subscribe(result => {
      this.subDivisionList = result || [];
      this.filterSubDivisionList = this.subDivisionList.slice();

      if (this.subDivisionId > 0 && this.subDivisionList.length > 1) {
        this.form.controls['subDivisionId'].setValue(this.subDivisionId);
        this.onSubDivisionChange();
      } else if (this.subDivisionList.length === 1) {
        this.form.controls['subDivisionId'].setValue(this.subDivisionList[0].key);
        this.onSubDivisionChange();
      }
    });
  }

  onSubDivisionChange(): void {
    this.resetFrom('villageId');
    this.divisionService.getvillageListbyId(true, this.form.value.subDivisionId).subscribe(result => {
      this.villageList = result || [];
      this.filterVillageList = this.villageList.slice();

      if (this.villageList.length === 1) {
        this.form.controls['villageId'].setValue(this.villageList[0].key);
      }
    });
  }

  onVillageChange(): void {
    this.resetGridState();
  }

  private departmentLookup(): void {
    this.vehicleService.getDepartmentLookupData(true).subscribe(result => {
      this.departmentList = result || [];
      this.filterDepartmentList = this.departmentList.slice();

      if (this.departmentList.length === 1) {
        this.form.controls['departmentId'].setValue(this.departmentList[0].key);
        this.onDepartmentChange();
      }
    });
  }

  onDepartmentChange(): void {
    this.resetSubsidyFrom('dbtGoId');
    this.goLookup();
  }

  private goLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    const departmentId = this.form.value.departmentId ?? 0;
    if (!financialYearId || !departmentId) {
      return;
    }
    this.paccsbookingService.getGoListbyId(financialYearId, departmentId, true).subscribe(result => {
      this.goList = result || [];
      this.filterGoList = this.goList.slice();

      if (this.goList.length === 1) {
        this.form.controls['dbtGoId'].setValue(this.goList[0].key);
        this.onGoChange();
      }
    });
  }

  onGoChange(): void {
    this.resetSubsidyFrom('dbtSchemeId');
    this.divisionService.getSchemaListbyId(true, this.form.value.dbtGoId).subscribe(result => {
      this.schemeList = result || [];
      this.filterSchemeList = this.schemeList.slice();

      if (this.schemeList.length === 1) {
        this.form.controls['dbtSchemeId'].setValue(this.schemeList[0].key);
        this.onSchemeChange();
      }
    });
  }

  onSchemeChange(): void {
    this.resetSubsidyFrom('dbtComponentId');
    this.divisionService.getComponetListbyId(this.form.value.dbtGoId, this.form.value.dbtSchemeId, true).subscribe(result => {
      this.componentList = result || [];
      this.filterComponentList = this.componentList.slice();

      if (this.componentList.length === 1) {
        this.form.controls['dbtComponentId'].setValue(this.componentList[0].key);
        this.onComponentChange();
      }
    });
  }

  onComponentChange(): void {
    this.resetSubsidyFrom('dbtProductId');
    const { financialYearId, departmentId, dbtGoId, dbtSchemeId, dbtComponentId } = this.form.value;
    this.divisionService.getfilteredProductList(true, financialYearId ?? 0, departmentId ?? 0, dbtGoId, dbtSchemeId, dbtComponentId)
      .subscribe(result => {
        this.productList = result || [];
        this.filterProductList = this.productList.slice();

        if (this.productList.length === 1) {
          this.form.controls['dbtProductId'].setValue(this.productList[0].key);
        }
      });
  }

  onProductChange(): void {
    this.resetGridState();
  }

  get selectedVillageName(): string {
    const village = this.villageList.find(v => v.key === this.form.value.villageId);
    return village ? village.value : '';
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.applicationsDataSource.filter = filterValue.trim().toLowerCase();
    if (this.applicationsDataSource.paginator) {
      this.applicationsDataSource.paginator.firstPage();
    }
  }

  onExportExcel(): void {
    if (!this.applications || this.applications.length === 0) {
      return;
    }

    const excelColumns = ['Application ID', 'Community'];
    const excelData = this.applications.map(a => ({
      ApplicationId: a.id,
      Community: a.community
    }));

    this.excelService.exportAsExcelFile(excelData, 'Lottery Applicants - ' + this.selectedVillageName, excelColumns);
  }

  showGrid(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoadingApplicants = true;
    this.fetchApplicants({
      next: () => {
        if (this.searchInput) {
          this.searchInput.nativeElement.value = '';
        }

        this.selectedParticipants = [];
        this.notSelectedParticipants = [];
        this.selectedDataSource = new MatTableDataSource<LotteryParticipant>([]);
        this.notSelectedDataSource = new MatTableDataSource<LotteryParticipant>([]);

        this.isShow = true;
        this.isResultShown = false;
        this.isLoadingApplicants = false;

        setTimeout(() => {
          this.applicationsDataSource.paginator = this.paginator;
          this.applicationsDataSource.sort = this.sort;
        });

        if (!this.noOfTargets || this.noOfTargets <= 0) {
          Swal.fire({
            title: 'No Remaining Target',
            text: 'No remaining target for the selected filters.',
            icon: 'warning'
          });
        }
      },
      error: () => {
        this.isLoadingApplicants = false;
      }
    });
  }

  private fetchApplicants(callbacks: { next: () => void; error: () => void }): void {
    const { financialYearId, regionId, divisionId, subDivisionId, villageId,
      departmentId, dbtGoId, dbtSchemeId, dbtComponentId, dbtProductId } = this.form.value;

    this.lotteryService.getBookingList(financialYearId, regionId, divisionId, subDivisionId, villageId,
      departmentId, dbtGoId, dbtSchemeId, dbtComponentId, dbtProductId, true).subscribe({
      next: (result: LotteryBookingListResponse) => {
        this.applications = (result?.beneficiaries || [])
          .map(b => ({ id: b.bookingRefNo, communityType: b.communityType, community: b.community }));
        this.applicationsDataSource = new MatTableDataSource<LotteryApplication>(this.applications);
        this.noOfTargets = result?.noOfTargets ?? 0;
        this.communityTargets = result?.communityTargets || [];
        callbacks.next();
      },
      error: callbacks.error
    });
  }

  runLottery(): void {
    if (this.applications.length === 0) {
      Swal.fire({ title: 'No Applicants', text: 'No applicants found for the selected village.', icon: 'warning' });
      return;
    }

    if (!this.noOfTargets || this.noOfTargets <= 0) {
      Swal.fire({
        title: 'No Remaining Target',
        text: 'No remaining target for the selected filters.',
        icon: 'error'
      });
      return;
    }

    const communityBreakdown = this.communityTargets
      .map(c => `${c.communityName}: ${c.remainingTarget}`)
      .join(', ');

    Swal.fire({
      title: 'Run Lottery?',
      text: `Run lottery for ${this.selectedVillageName} (${communityBreakdown || 'no community targets found'}), `
        + `${this.applications.length} applicant(s).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, run it',
      cancelButtonText: 'Cancel'
    }).then(res => {
      if (res.value) {
        this.executeLottery();
      }
    });
  }

  private executeLottery(): void {
    const { financialYearId, departmentId, dbtGoId, dbtSchemeId, dbtComponentId, dbtProductId } = this.form.value;
    const request = {
      applicants: this.applications.map(a => ({ applicationId: a.id, weight: 1 })),
      // No selectCount: each applicant's community and its winner cap are
      // resolved/computed server-side, per community - see lottery.service.ts.
      financialYearId,
      departmentId,
      dbtGoId,
      dbtSchemeId,
      dbtComponentId,
      dbtProductId
    };

    this.isRunningLottery = true;
    this.lotteryService.runLottery(request).subscribe({
      next: (response: { isSuccess: boolean; failures?: string[]; value?: { results: LotteryResultRow[]; communityTargets: CommunityLotteryRun[] } }) => {
        if (!response || !response.isSuccess) {
          this.isRunningLottery = false;
          const message = response?.failures?.length ? response.failures.join(', ') : 'Failed to run the lottery.';
          Swal.fire({ title: 'Lottery Failed', text: message, icon: 'error' });
          return;
        }

        const results = response.value?.results || [];
        this.communityResults = response.value?.communityTargets || [];
        const applicationById = new Map(this.applications.map(a => [a.id, a]));

        this.selectedParticipants = results
          .filter(r => r.selected)
          .map(r => ({
            id: r.applicationId,
            communityType: r.communityType ?? applicationById.get(r.applicationId)?.communityType,
            community: r.communityName || applicationById.get(r.applicationId)?.community,
            status: 'Selected'
          }));
        this.notSelectedParticipants = results
          .filter(r => !r.selected)
          .map(r => ({
            id: r.applicationId,
            communityType: r.communityType ?? applicationById.get(r.applicationId)?.communityType,
            community: r.communityName || applicationById.get(r.applicationId)?.community,
            status: 'Moved to Next Lottery'
          }));

        this.selectedDataSource = new MatTableDataSource<LotteryParticipant>(this.selectedParticipants);
        this.notSelectedDataSource = new MatTableDataSource<LotteryParticipant>(this.notSelectedParticipants);

        this.isShow = false;
        this.isResultShown = true;

        setTimeout(() => {
          this.selectedDataSource.paginator = this.selectedPaginator;
          this.notSelectedDataSource.paginator = this.notSelectedPaginator;
        });

        const communitySummary = this.communityResults
          .map(c => `${c.communityName}: ${c.selectedCount}/${c.targetCount}`)
          .join(', ');

        Swal.fire({
          title: 'Lottery Completed',
          text: `Lottery run successfully for ${this.selectedVillageName}. (${communitySummary})`,
          icon: 'success'
        });

        this.isRunningLottery = false;
      },
      error: () => {
        this.isRunningLottery = false;
        Swal.fire({ title: 'Lottery Failed', text: 'Something went wrong while running the lottery.', icon: 'error' });
      }
    });
  }

  clear(): void {
    this.regionList = [];
    this.filterRegionList = [];
    this.divisionList = [];
    this.filterDivisionList = [];
    this.subDivisionList = [];
    this.filterSubDivisionList = [];
    this.villageList = [];
    this.filterVillageList = [];
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.goList = [];
    this.filterGoList = [];
    this.schemeList = [];
    this.filterSchemeList = [];
    this.componentList = [];
    this.filterComponentList = [];
    this.productList = [];
    this.filterProductList = [];
    this.applications = [];
    this.selectedParticipants = [];
    this.notSelectedParticipants = [];
    this.applicationsDataSource = new MatTableDataSource<LotteryApplication>([]);
    this.selectedDataSource = new MatTableDataSource<LotteryParticipant>([]);
    this.notSelectedDataSource = new MatTableDataSource<LotteryParticipant>([]);
    this.noOfTargets = null;
    this.communityTargets = [];
    this.communityResults = [];
    this.isShow = false;
    this.isResultShown = false;
    this.initializeForm();
    this.getFinancialYear(true);
  }

  private resetFrom(controlName: 'regionId' | 'divisionId' | 'subDivisionId' | 'villageId'): void {
    const order = ['regionId', 'divisionId', 'subDivisionId', 'villageId'];
    const startIndex = order.indexOf(controlName);
    order.slice(startIndex).forEach(name => this.form.controls[name].setValue(null));

    if (startIndex <= 1) { this.divisionList = []; this.filterDivisionList = []; }
    if (startIndex <= 2) { this.subDivisionList = []; this.filterSubDivisionList = []; }
    if (startIndex <= 3) { this.villageList = []; this.filterVillageList = []; }

    this.resetGridState();
  }

  private resetSubsidyFrom(controlName: 'departmentId' | 'dbtGoId' | 'dbtSchemeId' | 'dbtComponentId' | 'dbtProductId'): void {
    const order = ['departmentId', 'dbtGoId', 'dbtSchemeId', 'dbtComponentId', 'dbtProductId'];
    const startIndex = order.indexOf(controlName);
    order.slice(startIndex).forEach(name => this.form.controls[name].setValue(null));

    if (startIndex <= 1) { this.goList = []; this.filterGoList = []; }
    if (startIndex <= 2) { this.schemeList = []; this.filterSchemeList = []; }
    if (startIndex <= 3) { this.componentList = []; this.filterComponentList = []; }
    if (startIndex <= 4) { this.productList = []; this.filterProductList = []; }

    this.resetGridState();
  }

  private resetGridState(): void {
    this.isShow = false;
    this.isResultShown = false;
    this.applications = [];
    this.selectedParticipants = [];
    this.notSelectedParticipants = [];
    this.noOfTargets = null;
    this.communityTargets = [];
    this.communityResults = [];
  }
}
